import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Select,
  SelectItem,
  Chip,
} from '@heroui/react';
import { TableMobileSize, UniversalTableProps } from '../types/account';
import Typography from './typography/Typography';
import { getButtonVariantColors } from '../theme';
import { useTranslations } from '../contexts/translation.context';
import { ArrowSortDown, ArrowSortUp, InfoCircle } from './icons';
import { ResponsiveTooltip } from './ui/ResponsiveTooltip';
import { useIsMobile } from '../hooks/useMobile';

const mobileTableWidthMap: Record<TableMobileSize, string> = {
  xs: 'w-[32rem]',
  sm: 'w-[48rem]',
  md: 'w-[60rem]',
  lg: 'w-[75rem]',
  xl: 'w-[87.5rem]',
  '2xl': 'w-[100rem]',
};

const UniversalTable = <T extends Record<string, any>>({
  data,
  columns,
  renderCell,
  onRowAction,
  emptyContent,
  isSearchEmpty,
  pagination,
  onPageChange,
  onPageSizeChange,
  currentPage = 1,
  currentPageSize = 5,
  sortBy,
  onSort,
  pageSizeOptions,
  mobileSize = 'lg',
}: UniversalTableProps<T>) => {
  const { t } = useTranslations();
  const isMobile = useIsMobile();

  const [currentSort, setCurrentSort] = useState(sortBy);
  const previousSortRef = useRef(sortBy);
  const previousPageRef = useRef(currentPage);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sortBy !== previousSortRef.current) {
      previousSortRef.current = sortBy;
      setCurrentSort(sortBy);
    }
  }, [sortBy]);

  // Функция для поиска ближайшего скроллируемого родителя
  const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
    if (!element) return null;

    let parent = element.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY || style.overflow;
      const overflowX = style.overflowX || style.overflow;

      if (
        overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowX === 'auto' ||
        overflowX === 'scroll'
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  };

  // Плавная функция easing для более естественной анимации
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  // Плавная прокрутка с кастомным easing
  const smoothScrollToTop = (element: HTMLElement | Window) => {
    const startPosition =
      element === window
        ? window.pageYOffset || document.documentElement.scrollTop
        : (element as HTMLElement).scrollTop;

    if (startPosition === 0) return;

    const duration = 600; // Увеличиваем длительность для более плавной анимации
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeInOutCubic(progress);

      const currentPosition = startPosition * (1 - eased);

      if (element === window) {
        window.scrollTo(0, currentPosition);
      } else {
        (element as HTMLElement).scrollTop = currentPosition;
      }

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  // Прокрутка вверх при смене страницы
  useEffect(() => {
    if (currentPage !== previousPageRef.current && previousPageRef.current !== undefined) {
      // Прокручиваем ближайший скроллируемый родительский контейнер
      const scrollableParent = findScrollableParent(tableContainerRef.current);
      if (scrollableParent) {
        smoothScrollToTop(scrollableParent);
      } else {
        // Если не найден скроллируемый родитель, пробуем прокрутить window
        smoothScrollToTop(window);
      }
      previousPageRef.current = currentPage;
    } else if (previousPageRef.current === undefined) {
      previousPageRef.current = currentPage;
    }
  }, [currentPage]);

  // Вычисляем общее количество страниц
  const totalPages = useMemo(() => {
    if (!pagination) return 0;
    return Math.ceil(pagination.count / pagination.page_size);
  }, [pagination]);

  const handlePageSizeChange = useCallback(
    (keys: any) => {
      const selectedValue = Array.from(keys)[0] as string;
      const newPageSize = Number(selectedValue);
      onPageSizeChange?.(newPageSize);
    },
    [onPageSizeChange]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      onPageChange?.(newPage);
    },
    [onPageChange]
  );

  const handleRowClick = useCallback(
    (item: T) => {
      if (onRowAction) {
        onRowAction(item);
      }
    },
    [onRowAction]
  );

  // Не используем useCallback - handleSort будет создаваться каждый раз с актуальным sortBy
  const handleSort = (column: (typeof columns)[0]) => {
    if (!column.sortable || !column.sortKey || !onSort) return;

    const sortKey = column.sortKey;
    console.log('[handleSort] Current sortBy:', currentSort);
    console.log('[handleSort] Column sortKey:', sortKey);

    setCurrentSort((prevSort) => {
      let nextSort: string;

      if (prevSort === sortKey) {
        nextSort = `-${sortKey}`;
        console.log('[handleSort] Switching to DESC');
      } else if (prevSort === `-${sortKey}`) {
        nextSort = sortKey;
        console.log('[handleSort] Switching to ASC');
      } else {
        nextSort = sortKey;
        console.log('[handleSort] Setting to ASC');
      }

      onSort(nextSort);
      return nextSort;
    });
  };

  const getSortIcon = useCallback(
    (column: (typeof columns)[0]) => {
      if (!column.sortable || !column.sortKey) return null;

      const isAsc = currentSort === column.sortKey;
      const isDesc = currentSort === `-${column.sortKey}`;

      const activeColor = '!text-partner';
      const inactiveColor = 'text-gray-300';

      if (isAsc) {
        return (
          <span className="ml-2 flex items-center leading-none py-1">
            <ArrowSortUp size={12} className={`transition-colors ${activeColor}`} />
          </span>
        );
      }

      if (isDesc) {
        return (
          <span className="ml-2 flex items-center leading-none py-1">
            <ArrowSortDown size={12} className={`transition-colors ${activeColor}`} />
          </span>
        );
      }

      return (
        <span className="ml-2 flex flex-col items-center leading-none py-1">
          <ArrowSortUp size={12} className={`transition-colors ${inactiveColor}`} />
          <ArrowSortDown size={12} className={`-mt-1 transition-colors ${inactiveColor}`} />
        </span>
      );
    },
    [currentSort]
  );

  const variantColors = getButtonVariantColors('primary');
  const isPartner = window.location.pathname.startsWith('/partner');

  return (
    <div ref={tableContainerRef} className="flex flex-col h-full relative pb-8">
      <div className="relative overflow-x-auto scroller-none px-4 pb-6 -mx-4 md:m-0 md:pb-6 md:p-0">
        <style>{`
          .table-row:hover .table-cell:first-child {
            background-color: #f9fafb;
            border-top-left-radius: 12px;
            border-bottom-left-radius: 12px;
          }
          .table-row:hover .table-cell:last-child {
            background-color: #f9fafb;
            border-top-right-radius: 12px;
            border-bottom-right-radius: 12px;
          }
          .table-row:hover .table-cell:not(:first-child):not(:last-child) {
            background-color: #f9fafb;
          }
        `}</style>
        <div className={`md:w-full ${mobileTableWidthMap[mobileSize]} shrink-0`}>
          <Table
            aria-label={t('aria.universalTable')}
            classNames={{
              wrapper:
                'bg-white rounded-2xl border border-gray-200 shadow-sm min-w-full overflow-hidden',
              // thead: "bg-gray-50",
              th: 'text-gray-500 font-medium text-sm h-12 px-4',
              td: 'py-4 px-4',
              tr: 'transition-colors table-row',
            }}
          >
            <TableHeader columns={columns}>
              {(column) => (
                <TableColumn
                  key={column.key}
                  width={column.width}
                  className={`text-gray-500 ${
                    column.key === columns[columns.length - 1]?.key ? 'relative' : ''
                  } ${column.sortable ? 'cursor-pointer hover:text-gray-700' : ''}`}
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center">
                    <span>{column.name}</span>
                    {column?.tooltip && (
                      <ResponsiveTooltip content={column.tooltip} placement="top">
                        <span className="ml-1 cursor-pointer text-gray-400">
                          <InfoCircle size={14} />
                        </span>
                      </ResponsiveTooltip>
                    )}
                    {column?.total && (
                      <Chip className="!ml-2.5 !max-h-5" size="sm" variant="flat" color="default">
                        <span>{column?.total}</span>
                      </Chip>
                    )}
                    {getSortIcon(column)}
                  </div>
                </TableColumn>
              )}
            </TableHeader>
            <TableBody
              items={data}
              emptyContent={
                emptyContent ||
                (isSearchEmpty ? (
                  <div className="text-center py-12">
                    <div className="flex flex-col items-center gap-4">
                      <div className="flex flex-col gap-1.5">
                        <div className="text-xl font-semibold leading-7">
                          {t('table.noDataFound')}
                        </div>
                        <div className="text-neutral-500 text-base font-medium leading-6">
                          {t('table.checkDataOrTryAgain')}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-500">{t('table.noDataToDisplay')}</div>
                  </div>
                ))
              }
            >
              {(item) => (
                <TableRow
                  className="h-[2.375rem]"
                  key={item.id || item.key || Math.random()}
                  onClick={() => handleRowClick(item)}
                  style={{ cursor: onRowAction ? 'pointer' : 'default' }}
                >
                  {(columnKey) => {
                    const isFirstColumn = columnKey === columns[0]?.key;
                    const isLastColumn = columnKey === columns[columns.length - 1]?.key;

                    return (
                      <TableCell
                        className={`align-top table-cell ${isLastColumn ? 'relative' : ''}`}
                      >
                        {renderCell(item, String(columnKey))}
                      </TableCell>
                    );
                  }}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {pagination && pagination.count > 0 && (
        <div className="absolute bottom-0 left-0 w-full flex md:flex-row flex-row-reverse items-center justify-between gap-1">
          <div className="flex items-center gap-2 md:gap-2.5">
            <Typography variant="body2" className="text-auth-muted max-md:text-xxs max-md:hidden">
              {t('table.displayPerPage')}
            </Typography>
            <Select
              size="sm"
              selectedKeys={[currentPageSize.toString()]}
              onSelectionChange={handlePageSizeChange}
              className="w-[3.875rem]"
              aria-label={t('aria.selectPageSize')}
              disallowEmptySelection
              classNames={{
                listboxWrapper: 'w-[4.5rem] px-1 py-0.5',
                value: 'gap-0',
                base: 'shrink-0',
              }}
            >
              {(pageSizeOptions || [10, 20, 50]).map((size) => (
                <SelectItem className="!gap-0" key={size.toString()} textValue={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </Select>
          </div>

          <Pagination
            isCompact
            total={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            showControls
            size="sm"
            siblings={isMobile ? 0 : undefined}
            boundaries={isMobile ? 1 : undefined}
            classNames={
              isPartner
                ? {
                    cursor: 'font-medium !bg-partner  !rounded-2 !text-white',
                    item: 'hover:!bg-gray-100',
                    wrapper: '!rounded-2',
                  }
                : {}
            }
          />
        </div>
      )}
    </div>
  );
};

export { UniversalTable };

const AccountTable = UniversalTable;

export default AccountTable;
